import React, { useState, useEffect } from 'react';

const AdminSponsorshipsPage = () => {
  const [sponsorships, setSponsorships] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [levelFilter, setLevelFilter] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [toast, setToast] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const csrf = () => document.querySelector('[name="csrf-token"]')?.content;

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  useEffect(() => { loadSponsorships(1); }, [statusFilter, levelFilter, sortBy]);

  const loadSponsorships = async (page = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, per_page: 15, sort: sortBy });
      if (statusFilter) params.append('status', statusFilter);
      if (levelFilter) params.append('level', levelFilter);

      const res = await fetch(`/api/v1/admin/sponsorships?${params}`, {
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf() }
      });
      const data = await res.json();
      if (data.success) {
        setSponsorships(data.data?.sponsorships || []);
        setPagination(data.data?.pagination);
      }
    } catch (e) {
      console.error(e);
      showToast('Erreur lors du chargement', 'error');
    } finally {
      setLoading(false);
    }
  };

  const forceApprove = async (id) => {
    if (!window.confirm('Confirmer l\'approbation forcée de cette cooptation ?')) return;
    setActionLoading(id);
    try {
      const res = await fetch(`/api/v1/admin/sponsorships/${id}/force_approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf() }
      });
      const data = await res.json();
      if (data.success) {
        showToast(data.message);
        loadSponsorships(pagination?.current_page || 1);
      } else {
        showToast(data.error || 'Erreur', 'error');
      }
    } catch (e) {
      showToast('Erreur réseau', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const forceReject = async (id) => {
    if (!window.confirm('Confirmer le rejet forcé de cette cooptation ?')) return;
    setActionLoading(id);
    try {
      const res = await fetch(`/api/v1/admin/sponsorships/${id}/force_reject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf() }
      });
      const data = await res.json();
      if (data.success) {
        showToast(data.message);
        loadSponsorships(pagination?.current_page || 1);
      } else {
        showToast(data.error || 'Erreur', 'error');
      }
    } catch (e) {
      showToast('Erreur réseau', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  // --- Styles ---
  const colors = {
    primary: '#7c2837', primaryDark: '#5a1d28', primaryLight: '#a8384e',
    bg: '#fdf8f9', card: '#ffffff', text: '#2c0e16', textMuted: '#6c4a53',
    border: '#e8d0d6', success: '#28a745', warning: '#ffc107', danger: '#dc3545', info: '#17a2b8',
  };

  const containerStyle = {
    maxWidth: 1200, margin: '0 auto', padding: '30px 20px',
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif", color: colors.text, minHeight: '80vh',
  };

  const headerStyle = {
    background: `linear-gradient(135deg, ${colors.primary}, ${colors.primaryDark})`,
    color: '#fff', padding: '35px 30px', borderRadius: 16, marginBottom: 30,
    display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 15,
  };

  const cardStyle = {
    background: colors.card, borderRadius: 12, padding: 25,
    boxShadow: '0 2px 12px rgba(124,40,55,0.08)', border: `1px solid ${colors.border}`, marginBottom: 16,
  };

  const btnStyle = (variant = 'primary') => {
    const map = {
      primary: { bg: colors.primary, color: '#fff' },
      success: { bg: colors.success, color: '#fff' },
      danger: { bg: colors.danger, color: '#fff' },
      outline: { bg: 'transparent', color: colors.primary, border: `1px solid ${colors.primary}` },
    };
    const v = map[variant] || map.primary;
    return {
      padding: '6px 14px', borderRadius: 6, border: v.border || 'none',
      background: v.bg, color: v.color, cursor: 'pointer', fontSize: 13, fontWeight: 600, transition: 'all 0.2s',
    };
  };

  const badgeStyle = (color) => ({
    display: 'inline-block', padding: '3px 10px', borderRadius: 12,
    fontSize: 12, fontWeight: 600, color: '#fff', background: color,
  });

  const toastStyle = {
    position: 'fixed', top: 20, right: 20, zIndex: 9999, padding: '14px 24px', borderRadius: 10,
    color: '#fff', fontWeight: 600, boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
    background: toast?.type === 'error' ? colors.danger : colors.success,
  };

  const statusMap = {
    pending: { label: 'En attente', color: colors.warning },
    approved: { label: 'Approuvée', color: colors.success },
    rejected: { label: 'Rejetée', color: colors.danger },
    completed: { label: 'Complétée', color: colors.info },
  };

  const getStatusInfo = (status) => statusMap[status] || { label: status, color: colors.textMuted };

  if (loading && (!sponsorships || sponsorships.length === 0)) {
    return (
      <div style={{ ...containerStyle, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>⏳</div>
          <p style={{ color: colors.textMuted, fontSize: 16 }}>Chargement des parrainages...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      {toast && <div style={toastStyle}>{toast.message}</div>}

      <div style={headerStyle}>
        <div>
          <h1 style={{ margin: 0, fontSize: 28 }}>👥 Gestion des Parrainages</h1>
          <p style={{ margin: '8px 0 0', opacity: 0.85, fontSize: 14 }}>
            {pagination ? `${pagination.total_count} cooptation(s) au total` : 'Administration des cooptations'}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <a href="/admin/dashboard" style={{ ...btnStyle('outline'), color: '#fff', borderColor: 'rgba(255,255,255,0.4)', textDecoration: 'none' }}>
            📊 Dashboard
          </a>
          <a href="/options" style={{ ...btnStyle('outline'), color: '#fff', borderColor: 'rgba(255,255,255,0.4)', textDecoration: 'none' }}>
            ← Paramètres
          </a>
        </div>
      </div>

      {/* Filters */}
      <div style={{ ...cardStyle, display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center' }}>
        <span style={{ fontWeight: 600, color: colors.primary }}>Filtres :</span>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          style={{ padding: '8px 12px', borderRadius: 8, border: `1px solid ${colors.border}`, fontSize: 14 }}>
          <option value="">Tous les statuts</option>
          <option value="pending">En attente</option>
          <option value="approved">Approuvées</option>
          <option value="rejected">Rejetées</option>
          <option value="completed">Complétées</option>
        </select>
        <select value={levelFilter} onChange={e => setLevelFilter(e.target.value)}
          style={{ padding: '8px 12px', borderRadius: 8, border: `1px solid ${colors.border}`, fontSize: 14 }}>
          <option value="">Tous les niveaux</option>
          <option value="invite">Invité</option>
          <option value="adherent_sympathisant">Sympathisant</option>
          <option value="adherent_participant">Participant</option>
          <option value="adherent_actif">Actif</option>
          <option value="membre_fondateur">Membre Fondateur</option>
        </select>
        <select value={sortBy} onChange={e => setSortBy(e.target.value)}
          style={{ padding: '8px 12px', borderRadius: 8, border: `1px solid ${colors.border}`, fontSize: 14 }}>
          <option value="newest">Plus récentes</option>
          <option value="oldest">Plus anciennes</option>
          <option value="status">Par statut</option>
        </select>
      </div>

      {/* Sponsorship list */}
      {!sponsorships || sponsorships.length === 0 ? (
        <div style={{ ...cardStyle, textAlign: 'center', padding: 50 }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>📭</div>
          <p style={{ color: colors.textMuted, fontSize: 16 }}>Aucune cooptation trouvée avec ces filtres</p>
        </div>
      ) : (
        sponsorships.map(sp => {
          const statusInfo = getStatusInfo(sp.status);
          const isExpanded = expandedId === sp.id;

          return (
            <div key={sp.id} style={{ ...cardStyle, cursor: 'pointer', borderLeft: `4px solid ${statusInfo.color}` }}
              onClick={() => setExpandedId(isExpanded ? null : sp.id)}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
                <div style={{ flex: '1 1 300px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                    <span style={{ fontWeight: 700, fontSize: 16 }}>
                      Cooptation #{sp.id}
                    </span>
                    <span style={badgeStyle(statusInfo.color)}>{statusInfo.label}</span>
                    {sp.requested_level && (
                      <span style={badgeStyle(colors.info)}>{sp.requested_level}</span>
                    )}
                  </div>
                  <div style={{ fontSize: 13, color: colors.textMuted }}>
                    {sp.initiator && (
                      <span>Initiée par <strong>{sp.initiator.first_name} {sp.initiator.last_name}</strong></span>
                    )}
                    {sp.filleul_email && (
                      <span> — Filleul : <strong>{sp.filleul_email}</strong></span>
                    )}
                  </div>
                  <div style={{ fontSize: 12, color: colors.textMuted, marginTop: 4 }}>
                    Créée le {new Date(sp.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }} onClick={e => e.stopPropagation()}>
                  {sp.status === 'pending' && (
                    <>
                      <button onClick={() => forceApprove(sp.id)} disabled={actionLoading === sp.id}
                        style={btnStyle('success')}>
                        ✅ Approuver
                      </button>
                      <button onClick={() => forceReject(sp.id)} disabled={actionLoading === sp.id}
                        style={btnStyle('danger')}>
                        ❌ Rejeter
                      </button>
                    </>
                  )}
                  <span style={{ fontSize: 18, color: colors.textMuted }}>{isExpanded ? '▲' : '▼'}</span>
                </div>
              </div>

              {isExpanded && (
                <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${colors.border}` }} onClick={e => e.stopPropagation()}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20 }}>
                    {sp.initiator && (
                      <div style={{ flex: '1 1 200px' }}>
                        <h4 style={{ marginTop: 0, color: colors.primary }}>🧑 Initiateur</h4>
                        <p style={{ margin: '4px 0' }}><strong>{sp.initiator.first_name} {sp.initiator.last_name}</strong></p>
                        <p style={{ margin: '4px 0', color: colors.textMuted, fontSize: 13 }}>{sp.initiator.email}</p>
                      </div>
                    )}
                    {sp.filleul_email && (
                      <div style={{ flex: '1 1 200px' }}>
                        <h4 style={{ marginTop: 0, color: colors.primary }}>🎓 Filleul</h4>
                        <p style={{ margin: '4px 0' }}><strong>{sp.filleul_email}</strong></p>
                        <p style={{ margin: '4px 0', color: colors.textMuted, fontSize: 13 }}>
                          Niveau demandé : {sp.requested_level}
                        </p>
                        <p style={{ margin: '4px 0', color: colors.textMuted, fontSize: 13 }}>
                          {sp.filleul_registered ? '✅ Inscrit' : '⏳ Pas encore inscrit'}
                        </p>
                      </div>
                    )}
                    {sp.supporters && sp.supporters.length > 0 && (
                      <div style={{ flex: '1 1 200px' }}>
                        <h4 style={{ marginTop: 0, color: colors.primary }}>🤝 Soutiens ({sp.supporters.length})</h4>
                        {sp.supporters.map((s, i) => (
                          <p key={i} style={{ margin: '4px 0', fontSize: 13 }}>
                            {s.first_name} {s.last_name}
                          </p>
                        ))}
                      </div>
                    )}
                    {sp.motivation && (
                      <div style={{ flex: '1 1 100%' }}>
                        <h4 style={{ marginTop: 0, color: colors.primary }}>💬 Motivation</h4>
                        <p style={{ margin: '4px 0', color: colors.textMuted, fontSize: 14, fontStyle: 'italic' }}>
                          "{sp.motivation}"
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })
      )}

      {/* Pagination */}
      {pagination && pagination.total_pages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 20, flexWrap: 'wrap' }}>
          {Array.from({ length: pagination.total_pages }, (_, i) => i + 1).map(p => (
            <button key={p} onClick={() => loadSponsorships(p)}
              style={{
                ...btnStyle(p === pagination.current_page ? 'primary' : 'outline'),
                minWidth: 36,
              }}>
              {p}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminSponsorshipsPage;
